import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  createAdminIdpMapping,
  deleteAdminIdpMapping,
  getAdminIdpMappings,
} from '../api';
import Button from './ui/Button';

/**
 * Which group of the identity provider grants which DiSI group.
 *
 * The provider decides who is in a group; this decides what the group means
 * here. Memberships follow at each person's next sign-in.
 */
function AdminIdpMappings({ groups }) {
  const { t } = useTranslation();

  const [mappings, setMappings] = useState([]);
  const [prefix, setPrefix] = useState(null);
  const [idpGroup, setIdpGroup] = useState('');
  const [groupId, setGroupId] = useState('');
  const [error, setError] = useState('');
  const [isBusy, setIsBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await getAdminIdpMappings();

      setMappings(data.mappings ?? []);
      setPrefix(data.groupPrefix ?? null);
      setIdpGroup((current) => current || data.groupPrefix || '/');
    } catch (err) {
      setError(err?.message || t('adminPanel.errorGeneric'));
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  async function handleAdd(event) {
    event.preventDefault();

    if (!groupId || idpGroup.trim().length < 2) {
      setError(t('adminPanel.idpMappingIncomplete'));
      return;
    }

    try {
      setIsBusy(true);
      setError('');

      await createAdminIdpMapping({
        idpGroup: idpGroup.trim(),
        groupId: Number(groupId),
      });

      setIdpGroup(prefix || '/');
      setGroupId('');
      await load();
    } catch (err) {
      setError(err?.message || t('adminPanel.errorGeneric'));
    } finally {
      setIsBusy(false);
    }
  }

  async function handleDelete(mappingId) {
    try {
      setIsBusy(true);
      setError('');

      await deleteAdminIdpMapping(mappingId);
      await load();
    } catch (err) {
      setError(err?.message || t('adminPanel.errorGeneric'));
    } finally {
      setIsBusy(false);
    }
  }

  return (
    <div className="admin-idp-mappings">
      {prefix === null && (
        <p className="admin-user-section-hint">
          {t('adminPanel.idpMappingsSsoOff')}
        </p>
      )}

      {error && (
        <div className="login-message login-message-error" role="alert">
          <i className="fa-solid fa-circle-exclamation" />
          <span>{error}</span>
        </div>
      )}

      {mappings.length === 0 ? (
        <p className="admin-user-list-empty">
          {t('adminPanel.idpMappingsEmpty')}
        </p>
      ) : (
        <ul className="admin-idp-mapping-list">
          {mappings.map((mapping) => (
            <li key={mapping.mappingId}>
              <code>{mapping.idpGroup}</code>
              <i className="fa-solid fa-arrow-right" aria-hidden="true" />
              <span>
                {mapping.groupName}
                <small> · {mapping.departmentName}</small>
              </span>

              <Button
                variant="text"
                disabled={isBusy}
                aria-label={t('adminPanel.idpMappingDelete', {
                  path: mapping.idpGroup,
                })}
                onClick={() => handleDelete(mapping.mappingId)}
              >
                <i className="fa-solid fa-trash" />
              </Button>
            </li>
          ))}
        </ul>
      )}

      <form className="account-form" onSubmit={handleAdd}>
        <div className="account-field-row">
          <label htmlFor="adminIdpGroupPath">
            {t('adminPanel.idpGroupPathLabel')}
          </label>

          <input
            id="adminIdpGroupPath"
            type="text"
            value={idpGroup}
            placeholder="/disi/Accounting/Autoren"
            disabled={isBusy}
            onChange={(event) => {
              setIdpGroup(event.target.value);
              setError('');
            }}
          />
        </div>

        <div className="account-field-row">
          <label htmlFor="adminIdpTargetGroup">
            {t('adminPanel.idpTargetGroupLabel')}
          </label>

          <select
            id="adminIdpTargetGroup"
            value={groupId}
            disabled={isBusy}
            onChange={(event) => {
              setGroupId(event.target.value);
              setError('');
            }}
          >
            <option value="">{t('adminPanel.idpTargetGroupPlaceholder')}</option>
            {groups.map((group) => (
              <option key={group.group_id} value={group.group_id}>
                {group.group_name} ·{' '}
                {group.is_platform
                  ? t('adminPanel.platformScope')
                  : group.department_name}
              </option>
            ))}
          </select>
        </div>

        <div className="account-actions">
          <Button variant="primary" type="submit" disabled={isBusy}>
            {t('adminPanel.idpMappingAdd')}
          </Button>
        </div>
      </form>
    </div>
  );
}

export default AdminIdpMappings;
